import { useState } from "react";

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  grabacionUrl,
  useZoomColas,
  useZoomDemanda,
  useZoomEstado,
  useZoomFiltros,
  useZoomLlamadas,
  useZoomPosiciones,
  type FiltroLlamadas,
  type Rango,
  type ZoomCola,
} from "@/hooks/useZoom";
import { ZoomConfigTab } from "@/components/ZoomConfigTab";
import { ZoomPlanTab } from "@/components/ZoomPlanTab";
import { toLocalIsoDate } from "@/lib/localDate";

// Consola "Zoom tiLatina" (fase 2 de la migración Asterisk → Zoom). Sólo
// lectura: lo que AROC copia del historial de Zoom Phone cada 5 minutos.

type Pestana =
  "hoy" | "demanda" | "posiciones" | "llamadas" | "configuracion" | "plan";

const PESTANAS: { clave: Pestana; etiqueta: string }[] = [
  { clave: "hoy", etiqueta: "Hoy" },
  { clave: "demanda", etiqueta: "Demanda" },
  { clave: "posiciones", etiqueta: "Posiciones" },
  { clave: "llamadas", etiqueta: "Llamadas" },
  { clave: "configuracion", etiqueta: "Configuración" },
  { clave: "plan", etiqueta: "Plan vs Zoom" },
];

const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const RESULTADO: Record<string, string> = {
  answered: "Contestada",
  abandoned: "Abandonada",
  no_answer: "Sin contestar",
  hang_up: "Colgó",
  connected: "Conectada",
};

function haceDias(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toLocalIsoDate(d);
}

const mmss = (s: number) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const SEMAFORO: Record<ZoomCola["semaforo"], string> = {
  verde: "border-l-emerald-500",
  amarillo: "border-l-amber-500",
  rojo: "border-l-red-500",
};

function SelectorRango({ r, set }: { r: Rango; set: (r: Rango) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">Del</span>
      <Input
        type="date"
        className="w-40"
        value={r.desde}
        onChange={(e) => set({ ...r, desde: e.target.value })}
      />
      <span className="text-muted-foreground">al</span>
      <Input
        type="date"
        className="w-40"
        value={r.hasta}
        onChange={(e) => set({ ...r, hasta: e.target.value })}
      />
      {[
        ["Hoy", 0],
        ["7 días", 6],
        ["30 días", 29],
      ].map(([t, n]) => (
        <Button
          key={t}
          variant="outline"
          size="sm"
          onClick={() =>
            set({ desde: haceDias(Number(n)), hasta: haceDias(0) })
          }
        >
          {t}
        </Button>
      ))}
    </div>
  );
}

function Hoy({ r }: { r: Rango }) {
  const q = useZoomColas(r);
  if (q.isPending)
    return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (q.isError)
    return (
      <p className="text-sm text-red-600">No se pudo leer: {String(q.error)}</p>
    );
  const { colas, salientes } = q.data;
  return (
    <div className="space-y-4">
      {colas.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Sin llamadas entrantes en el periodo.
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {colas.map((c) => (
          <Card
            key={c.cola_ext}
            className={`border-l-4 ${SEMAFORO[c.semaforo]}`}
          >
            <CardHeader className="pb-2">
              <CardTitle className="flex items-baseline justify-between text-base">
                <span>{c.cola || `Cola ${c.cola_ext}`}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  ext {c.cola_ext}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
                <div className="shrink-0">
                  <div className="text-3xl font-semibold tabular-nums">
                    {c.abandono_pct}%
                  </div>
                  <div className="text-xs text-muted-foreground">
                    se colgaron esperando
                  </div>
                </div>
                <dl className="grid min-w-0 grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm tabular-nums">
                  <dt className="whitespace-nowrap text-muted-foreground">
                    Entraron
                  </dt>
                  <dd>{c.entradas}</dd>
                  <dt className="whitespace-nowrap text-muted-foreground">
                    Contestadas
                  </dt>
                  <dd>{c.contestadas}</dd>
                  <dt className="whitespace-nowrap text-muted-foreground">
                    Abandonadas
                  </dt>
                  <dd>{c.abandonadas}</dd>
                  <dt className="whitespace-nowrap text-muted-foreground">
                    Espera prom.
                  </dt>
                  <dd className="flex flex-wrap gap-x-1">
                    <span className="whitespace-nowrap">
                      {mmss(c.espera_prom_s)}
                    </span>
                    <span className="whitespace-nowrap text-xs text-muted-foreground">
                      (máx {mmss(c.espera_max_s)})
                    </span>
                  </dd>
                </dl>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <p className="text-sm text-muted-foreground">
        Salientes en el periodo:{" "}
        <b className="text-foreground">{salientes.total}</b> (
        {salientes.contestadas} contestadas).
      </p>
    </div>
  );
}

type ModoDemanda = "todo" | "colas" | "exts";

// Chips de selección múltiple para el filtro de Demanda.
function Chips({
  opciones,
  sel,
  set,
}: {
  opciones: { ext: string; nombre: string }[];
  sel: string[];
  set: (v: string[]) => void;
}) {
  if (opciones.length === 0)
    return (
      <span className="text-sm text-muted-foreground">
        Sin datos en el periodo.
      </span>
    );
  return (
    <div className="flex flex-wrap gap-1">
      {opciones.map((o) => {
        const activo = sel.includes(o.ext);
        return (
          <Button
            key={o.ext}
            size="sm"
            variant={activo ? "default" : "outline"}
            onClick={() =>
              set(activo ? sel.filter((x) => x !== o.ext) : [...sel, o.ext])
            }
          >
            {o.nombre || o.ext} <span className="ml-1 opacity-60">{o.ext}</span>
          </Button>
        );
      })}
    </div>
  );
}

// Una línea por día; el fin de semana punteado y el promedio, más grueso.
const COLOR_DIA = [
  "#2563eb",
  "#16a34a",
  "#f59e0b",
  "#9333ea",
  "#dc2626",
  "#64748b",
  "#94a3b8",
];

function Demanda({ r }: { r: Rango }) {
  const [modo, setModo] = useState<ModoDemanda>("todo");
  const [colas, setColas] = useState<string[]>([]);
  const [exts, setExts] = useState<string[]>([]);
  const filtros = useZoomFiltros(r);
  const q = useZoomDemanda(r, {
    colas: modo === "colas" ? colas : [],
    exts: modo === "exts" ? exts : [],
  });

  const barra = (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1">
        {(
          [
            ["todo", "Todas las colas"],
            ["colas", "Por área (cola)"],
            ["exts", "Por posición"],
          ] as const
        ).map(([m, t]) => (
          <Button
            key={m}
            size="sm"
            variant={modo === m ? "default" : "ghost"}
            onClick={() => setModo(m)}
          >
            {t}
          </Button>
        ))}
      </div>
      {modo === "colas" && (
        <Chips
          opciones={filtros.data?.colas ?? []}
          sel={colas}
          set={setColas}
        />
      )}
      {modo === "exts" && (
        <Chips
          opciones={filtros.data?.posiciones ?? []}
          sel={exts}
          set={setExts}
        />
      )}
    </div>
  );

  if (q.isPending)
    return (
      <div className="space-y-3">
        {barra}
        <p className="text-sm text-muted-foreground">Cargando…</p>
      </div>
    );
  if (q.isError)
    return (
      <p className="text-sm text-red-600">No se pudo leer: {String(q.error)}</p>
    );
  const { llamadas, abandonos } = q.data;
  const max = Math.max(1, ...llamadas.flat());
  const porPosicion = modo === "exts";
  // Las 24 horas, para ver los picos donde caigan.
  const puntos = Array.from({ length: 24 }, (_, h) => {
    const p: Record<string, number> = { h };
    let suma = 0;
    COLOR_DIA.forEach((_, d) => {
      p[DIAS[d]] = llamadas[d][h];
      p[`ab${d}`] = abandonos[d][h];
      suma += llamadas[d][h];
    });
    p.Promedio = Math.round((suma / COLOR_DIA.length) * 10) / 10;
    return p;
  });
  // El pico: la hora y el día con más llamadas, y el día más cargado.
  const pico = llamadas
    .flatMap((fila, d) => fila.map((n, h) => ({ d, h, n })))
    .reduce((a, b) => (b.n > a.n ? b : a), { d: 0, h: 0, n: 0 });
  const porDia = llamadas.map((fila) => fila.reduce((a, b) => a + b, 0));
  const diaFuerte = porDia.indexOf(Math.max(...porDia));
  const porHora = Array.from({ length: 24 }, (_, h) =>
    llamadas.reduce((a, fila) => a + fila[h], 0),
  );
  const horaFuerte = porHora.indexOf(Math.max(...porHora));

  return (
    <div className="space-y-6">
      {barra}
      {pico.n > 0 && (
        <div className="flex flex-wrap gap-3 text-sm">
          <Badge variant="secondary">
            Pico: {DIAS[pico.d]} {pico.h}:00 · {pico.n} llamadas
          </Badge>
          <Badge variant="secondary">
            Hora más cargada: {horaFuerte}:00 · {porHora[horaFuerte]}
          </Badge>
          <Badge variant="secondary">
            Día más cargado: {DIAS[diaFuerte]} · {porDia[diaFuerte]}
          </Badge>
        </div>
      )}
      <div>
        <p className="mb-2 text-sm text-muted-foreground">
          {porPosicion
            ? "Llamadas que esas posiciones contestaron o marcaron. Las abandonadas no las tuvo ninguna posición: en este modo no se cuentan."
            : "Llamadas entrantes por hora, las 24 horas, una línea por día (sábado y domingo punteados); la gruesa es el promedio. El % que se colgó esperando sale al pasar el cursor."}
        </p>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={puntos}
            margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
          >
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
            <XAxis
              dataKey="h"
              interval={1}
              tickFormatter={(h: number) => `${h}:00`}
              stroke="#94a3b8"
              fontSize={11}
            />
            <YAxis
              allowDecimals={false}
              stroke="#94a3b8"
              fontSize={11}
              width={36}
            />
            <Tooltip
              content={({ payload, label }) =>
                payload?.length ? (
                  <div className="rounded border bg-background px-2 py-1 text-xs shadow">
                    <div className="font-medium">{label}:00</div>
                    {payload.map((x) => {
                      const d = DIAS.indexOf(String(x.dataKey));
                      const n = Number(x.value);
                      const ab = d >= 0 ? Number(x.payload[`ab${d}`]) : 0;
                      return (
                        <div key={String(x.dataKey)} style={{ color: x.color }}>
                          {x.dataKey}: {n} llamadas
                          {!porPosicion &&
                            d >= 0 &&
                            n > 0 &&
                            ` · ${Math.round((ab / n) * 100)}% abandonadas`}
                        </div>
                      );
                    })}
                  </div>
                ) : null
              }
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {COLOR_DIA.map((c, d) => (
              <Line
                key={d}
                type="monotone"
                dataKey={DIAS[d]}
                stroke={c}
                strokeWidth={1.5}
                strokeDasharray={d >= 5 ? "4 3" : undefined}
                dot={false}
              />
            ))}
            <Line
              type="monotone"
              dataKey="Promedio"
              stroke="#0f172a"
              strokeWidth={3.5}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto">
        <p className="mb-3 text-sm text-muted-foreground">
          Mapa por día y hora. Más oscuro = más llamadas
          {porPosicion
            ? "."
            : "; el borde rojo marca la hora en que más de la mitad se colgó esperando."}
        </p>
        <table className="border-separate border-spacing-0.5 text-xs">
          <thead>
            <tr>
              <th />
              {Array.from({ length: 24 }, (_, h) => (
                <th key={h} className="w-7 font-normal text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {llamadas.map((fila, d) => (
              <tr key={d}>
                <th className="pr-2 text-right font-normal text-muted-foreground">
                  {DIAS[d]}
                </th>
                {fila.map((n, h) => {
                  const ab = abandonos[d][h];
                  return (
                    <td
                      key={h}
                      title={`${DIAS[d]} ${h}:00 — ${n} llamadas${porPosicion ? "" : `, ${ab} abandonadas`}`}
                      className={`h-7 w-7 rounded text-center tabular-nums ${
                        n && ab / n > 0.5 ? "ring-2 ring-red-500" : ""
                      }`}
                      style={{
                        background: n
                          ? `rgba(37, 99, 235, ${0.12 + 0.88 * (n / max)})`
                          : "var(--muted, #f1f5f9)",
                        color: n / max > 0.55 ? "white" : undefined,
                      }}
                    >
                      {n || ""}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Posiciones({ r }: { r: Rango }) {
  const q = useZoomPosiciones(r);
  if (q.isPending)
    return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (q.isError)
    return (
      <p className="text-sm text-red-600">No se pudo leer: {String(q.error)}</p>
    );
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Ext</TableHead>
          <TableHead>Nombre</TableHead>
          <TableHead>Licencia</TableHead>
          <TableHead className="text-right">Contestó</TableHead>
          <TableHead className="text-right">Le sonó y no contestó</TableHead>
          <TableHead className="text-right">% contesta</TableHead>
          <TableHead className="text-right">Salientes</TableHead>
          <TableHead className="text-right">Minutos</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {q.data.posiciones.map((p) => (
          <TableRow key={p.ext} className={p.sin_uso ? "opacity-60" : ""}>
            <TableCell className="tabular-nums">{p.ext}</TableCell>
            <TableCell>
              {p.nombre} {p.sin_uso && <Badge variant="outline">sin uso</Badge>}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {p.licencia}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {p.contestadas}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {p.sin_contestar}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {p.contesta_pct === null ? "—" : `${p.contesta_pct}%`}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {p.salientes}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {p.minutos}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function Llamadas({ r }: { r: Rango }) {
  const [f, setF] = useState<Omit<FiltroLlamadas, keyof Rango>>({ pagina: 1 });
  const [oyendo, setOyendo] = useState<string | null>(null);
  const q = useZoomLlamadas({ ...r, ...f });
  const cambia = (k: keyof FiltroLlamadas, v: string) =>
    setF({ ...f, [k]: v, pagina: 1 });
  const paginas = q.data
    ? Math.max(1, Math.ceil(q.data.total / q.data.por_pagina))
    : 1;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Input
          className="w-44"
          placeholder="Número"
          onChange={(e) => cambia("numero", e.target.value)}
        />
        <Input
          className="w-28"
          placeholder="Cola (806)"
          onChange={(e) => cambia("cola", e.target.value)}
        />
        <Input
          className="w-28"
          placeholder="Ext agente"
          onChange={(e) => cambia("agente", e.target.value)}
        />
        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          onChange={(e) => cambia("resultado", e.target.value)}
        >
          <option value="">Todos los resultados</option>
          {Object.entries(RESULTADO).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <span className="self-center text-sm text-muted-foreground">
          {q.data?.total ?? 0} llamadas
        </span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Fecha</TableHead>
            <TableHead />
            <TableHead>Número</TableHead>
            <TableHead>Cola</TableHead>
            <TableHead>Agente</TableHead>
            <TableHead>Resultado</TableHead>
            <TableHead className="text-right">Espera</TableHead>
            <TableHead className="text-right">Duración</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {q.data?.items.map((l) => (
            <TableRow key={l.id}>
              <TableCell className="whitespace-nowrap tabular-nums">
                {new Date(l.inicio).toLocaleString("es-MX", {
                  dateStyle: "short",
                  timeStyle: "short",
                })}
              </TableCell>
              <TableCell title={l.direccion}>
                {l.direccion === "entrante" ? "↙" : "↗"}
              </TableCell>
              <TableCell className="tabular-nums">{l.numero_externo}</TableCell>
              <TableCell>{l.cola}</TableCell>
              <TableCell>
                {l.agente_ext && `${l.agente_ext} ${l.agente}`}
                {l.sin_contestar.length > 0 && (
                  <span className="block text-xs text-muted-foreground">
                    no contestó: {l.sin_contestar.join(", ")}
                  </span>
                )}
              </TableCell>
              <TableCell>
                <Badge
                  variant={
                    l.resultado === "abandoned" ? "destructive" : "secondary"
                  }
                >
                  {RESULTADO[l.resultado] ?? l.resultado}
                </Badge>
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {l.espera_s ? mmss(l.espera_s) : ""}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {mmss(l.duracion_s)}
              </TableCell>
              <TableCell>
                {l.con_grabacion &&
                  (oyendo === l.id ? (
                    <audio
                      controls
                      autoPlay
                      src={grabacionUrl(l.id)}
                      className="h-8 w-56"
                    />
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOyendo(l.id)}
                    >
                      Escuchar
                    </Button>
                  ))}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex items-center gap-2 text-sm">
        <Button
          variant="outline"
          size="sm"
          disabled={(f.pagina ?? 1) <= 1}
          onClick={() => setF({ ...f, pagina: (f.pagina ?? 1) - 1 })}
        >
          Anterior
        </Button>
        <span className="tabular-nums">
          {f.pagina ?? 1} / {paginas}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={(f.pagina ?? 1) >= paginas}
          onClick={() => setF({ ...f, pagina: (f.pagina ?? 1) + 1 })}
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
}

export default function ZoomPage() {
  const [pestana, setPestana] = useState<Pestana>("hoy");
  const [hoy, setHoy] = useState<Rango>({
    desde: haceDias(0),
    hasta: haceDias(0),
  });
  const [rango, setRango] = useState<Rango>({
    desde: haceDias(29),
    hasta: haceDias(0),
  });
  const estado = useZoomEstado();
  const r = pestana === "hoy" ? hoy : rango;
  const setR = pestana === "hoy" ? setHoy : setRango;
  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Zoom tiLatina</h1>
          <p className="text-sm text-muted-foreground">
            Telefonía Zoom Phone: colas, demanda y posiciones, para decidir si
            subimos o bajamos.
          </p>
        </div>
        {estado.data && (
          <p className="text-xs text-muted-foreground">
            {estado.data.llamadas} llamadas guardadas · sincronización{" "}
            {estado.data.sincronizacion}
            {estado.data.ultima_vuelta.en &&
              ` · última ${new Date(estado.data.ultima_vuelta.en).toLocaleTimeString("es-MX")}`}
            {estado.data.ultima_vuelta.ok === false && (
              <span className="text-red-600">
                {" "}
                · falló: {estado.data.ultima_vuelta.error}
              </span>
            )}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-2">
        <div className="flex gap-1">
          {PESTANAS.map((p) => (
            <Button
              key={p.clave}
              variant={pestana === p.clave ? "default" : "ghost"}
              size="sm"
              onClick={() => setPestana(p.clave)}
            >
              {p.etiqueta}
            </Button>
          ))}
        </div>
        {pestana !== "configuracion" && pestana !== "plan" && (
          <SelectorRango r={r} set={setR} />
        )}
      </div>
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>
            {pestana === "configuracion"
              ? "Cómo está armado Zoom: se fotografía una vez al día"
              : pestana === "plan"
                ? "Cómo debe quedar Zoom contra la última foto; cada cambio pasa por Aprobaciones"
                : `${r.desde === r.hasta ? r.desde : `${r.desde} → ${r.hasta}`} · hora de CDMX`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pestana === "hoy" && <Hoy r={r} />}
          {pestana === "demanda" && <Demanda r={r} />}
          {pestana === "posiciones" && <Posiciones r={r} />}
          {pestana === "llamadas" && <Llamadas r={r} />}
          {pestana === "configuracion" && <ZoomConfigTab />}
          {pestana === "plan" && <ZoomPlanTab />}
        </CardContent>
      </Card>
    </div>
  );
}
